import type { Address } from './index.js';

/** Contacts: addresses collected from mail (for suggesting recipients when composing). */
export interface Contact {
  name: string;
  address: string;
  /** Number of messages I sent them (To, Cc and Bcc all count). */
  sent: number;
  /** Number of messages they sent me (spam and deleted don't count). */
  received: number;
  /** Time of the most recent exchange (ms). */
  last: number;
}

const DAY = 86_400_000;

/**
 * The contact's own weight: people I've written to always rank above those I've only received from (receive-only ones are mostly merchants and bulk mail);
 * for receive-only contacts, received count is capped at 5 (otherwise daily advertisers would rank first); recent exchanges weigh more.
 */
export function contactWeight(c: Contact, now = Date.now()): number {
  const age = now - c.last;
  const recent = age < 30 * DAY ? 2 : age < 365 * DAY ? 1 : 0;
  return (c.sent > 0 ? 10 + Math.log2(1 + c.sent) : Math.log2(1 + Math.min(c.received, 5))) + recent;
}

/** Bulk / notification addresses: don't suggest them unless I've written to them. */
export function isNoReply(address: string): boolean {
  const local = address.split('@')[0]?.toLowerCase() ?? '';
  return /^(no[-_.]?reply|do[-_.]?not[-_.]?reply|mailer[-_.]?daemon|postmaster|bounces?|notifications?|notify|alerts?|newsletters?|news|info|support|service|system)([-_.+].*)?$/.test(local);
}

/** How well the input matches a contact: 0 = no match; start of the whole thing > start of a word > middle. */
export function matchQuality(c: Pick<Contact, 'name' | 'address'>, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const name = c.name.toLowerCase();
  const addr = c.address.toLowerCase();
  if (name.startsWith(q) || addr.startsWith(q)) return 3;
  const words = [...name.split(/[\s.,_\-'"()（）·]+/), ...addr.split('@')[0]!.split(/[._\-+]+/), addr.split('@')[1] ?? ''];
  if (words.some((w) => w && w.startsWith(q))) return 2;
  if (name.includes(q) || addr.includes(q)) return 1;
  return 0;
}

/** Pick contacts for the input: people I've written to first; then by match quality (start > word start > middle), then by how many and how recent the exchanges. exclude: addresses already filled in. */
export function suggestContacts(contacts: Contact[], query: string, exclude: Iterable<string> = [], limit = 6, now = Date.now()): Contact[] {
  const taken = new Set([...exclude].map((a) => a.toLowerCase()));
  const scored: { c: Contact; m: number; w: number }[] = [];
  for (const c of contacts) {
    if (taken.has(c.address.toLowerCase())) continue;
    const m = matchQuality(c, query);
    if (m) scored.push({ c, m, w: contactWeight(c, now) });
  }
  const wrote = (c: Contact) => (c.sent > 0 ? 1 : 0);
  scored.sort((a, b) => wrote(b.c) - wrote(a.c) || b.m - a.m || b.w - a.w || a.c.address.localeCompare(b.c.address));
  return scored.slice(0, limit).map((s) => s.c);
}

/** When there's only an address (e.g. collected from the message list at hand), treat it as a contact with one exchange. */
export function contactFromAddress(a: Address, last = 0): Contact {
  return { name: a.name, address: a.address, sent: 0, received: 1, last };
}
